// Shared Plan logic: day grouping, streaks, 5-day cuts and extraction cancellation

const parseTs = (s) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? 0 : d.getTime();
};

// Normalize any date string (09/30/26, 09/30/2026, 2026-09-30...) to MM/DD/YYYY
// so trades of the same day always group together
const normalizeDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

export const buildDayGroups = (bitacoraEntries = [], estudioEntries = []) => {
  const ordered = [...bitacoraEntries, ...estudioEntries]
    .slice()
    .sort((a, b) => parseTs(a.date) - parseTs(b.date)); // oldest first
  const map = new Map();
  ordered.forEach((e) => {
    const key = normalizeDate(e.date) || `sin-fecha-${e.id}`;
    if (!map.has(key)) map.set(key, { date: normalizeDate(e.date), entries: [] });
    map.get(key).entries.push(e);
  });
  return [...map.values()].map((g) => ({
    ...g,
    profit: g.entries.reduce((s, e) => s + (e.profit || 0), 0),
    investedReal: g.entries.reduce((s, e) => s + (e.totalInvested || 0), 0),
    investment: g.entries.find((e) => e.investment)?.investment || 0
  }));
};

// Computes executed plan rows with streak logic, 5-day cuts and the rule:
// a losing day absorbs UNCONFIRMED extractions from previous cuts (oldest first).
// If the loss wipes a pending extraction it is cancelled; if smaller, it is reduced.
// Only the leftover loss (if any) carries into the new cycle.
export const computeExecutedRows = ({ dayGroups, percentage, initialCapital = 0, confirmedExtractions = {}, startingBalance = null }) => {
  const rows = [];
  // Real account total at the START of each day (capital + deposits + adjustment when provided)
  let runningAccount = startingBalance ?? initialCapital;
  let positiveStreak = 0;
  let cycleProfitPlus = 0;
  const pendingCuts = []; // references to rows with unconfirmed extractions

  dayGroups.forEach((dayGroup, index) => {
    const stored = Number(dayGroup.investment) || 0;
    // Inversión = planned investment or, by default, the available balance (total/3) of that day
    const investment = stored > 0 ? stored : Math.max(0, runningAccount / 3);
    const realProfit = dayGroup.profit;
    // On a losing day nothing was earned: the expected 10% profit is 0
    const expectedProfit = realProfit <= 0 ? 0 : investment * (percentage / 100);
    // Profit+ can never be negative: if the real profit is below the expected one it's 0
    const profitPlus = Math.max(0, realProfit - expectedProfit);

    positiveStreak = realProfit > 0 ? positiveStreak + 1 : 0;
    if (realProfit <= 0) {
      // Losing day: the REAL loss eats pending unconfirmed extractions first
      let lossReal = realProfit; // negative (actual money lost)
      const cancelledNow = [];
      pendingCuts.forEach((cut) => {
        if (lossReal >= 0 || cut.extraction <= 0) return;
        const absorb = Math.min(cut.extraction, -lossReal);
        cut.extraction = Number((cut.extraction - absorb).toFixed(2));
        lossReal += absorb;
        if (cut.extraction <= 0.005) {
          cut.extraction = 0;
          cut.extractionCancelled = true;
          cut.extractionReduced = false;
          cancelledNow.push(cut);
        } else {
          cut.extractionReduced = true;
        }
      });
      // Cancelled cuts display the resulting negative balance (user request)
      cancelledNow.forEach((cut) => { cut.cutBalance = lossReal; });
      for (let i = pendingCuts.length - 1; i >= 0; i--) {
        if (pendingCuts[i].extraction === 0) pendingCuts.splice(i, 1);
      }
      // The remaining loss does NOT carry into the next cycle: each new streak starts clean
      cycleProfitPlus = 0;
    } else {
      cycleProfitPlus += profitPlus;
    }

    const row = {
      index: index + 1,
      investment,
      expectedProfit,
      // Total Cuenta mirrors the real broker balance: full real profit adds (incl. Profit+),
      // losses subtract in full, and confirmed extractions are discounted on their row
      accountDelta: realProfit - (confirmedExtractions[index + 1]?.amount || 0),
      dayGroup,
      date: dayGroup.date || '',
      realProfit,
      investedReal: dayGroup.investedReal || 0,
      opsCount: dayGroup.entries.length || 0,
      profitPlus,
      extraction: 0,
      originalExtraction: 0,
      extractionCancelled: false,
      extractionReduced: false,
      cutBalance: null,
      streak: positiveStreak,
      streakBroken: realProfit <= 0,
      isExecuted: true,
      isCurrentTrade: false
    };

    // Cut at every 5th CONSECUTIVE positive day
    if (positiveStreak > 0 && positiveStreak % 5 === 0) {
      row.cutBalance = cycleProfitPlus;
      if (cycleProfitPlus > 0) {
        row.extraction = cycleProfitPlus;
        row.originalExtraction = cycleProfitPlus;
        if (!confirmedExtractions[row.index]) pendingCuts.push(row);
      }
      cycleProfitPlus = 0;
    }

    rows.push(row);
    runningAccount += row.accountDelta;
  });

  return rows;
};
